/* =====================================================================
   DIGITAL TWIN — CALCULATIONS (pure functions, no React, no drawing)

   Everything the visitor sees in the digital twin comes from these
   functions applied to one snapshot (sampleData.js today, an API later).

   Model: a sewing line is a chain of operations. Every garment passes
   every station in order, so the line can only go as fast as its slowest
   station — the bottleneck.

     capacity  (pcs/h)  = operators × 60 / SMV × performance
     availability       = 1 − downtimeMin / elapsedMin
     effective capacity = capacity × availability
     line rate (pcs/h)  = lowest effective capacity on the line
     completed so far   = station i can't finish more than the slowest
                          station up to and including i has allowed
                          = floor(min(effCap[0..i]) × elapsed hours)
                          and no more than `wipLimit` pieces ahead of the
                          next station (stations upstream get blocked)
     WIP before i       = completed[i−1] − completed[i]
     utilisation        = line rate / effective capacity
     projected output   = output so far + line rate × remaining hours
     balance (LBE %)    = ΣSMV / (operators × slowest cycle time) × 100,
                          cycle time = SMV / operators   (standard minutes)

   Assumptions (also explained on the page and in docs/DIGITAL_TWIN.md):
   the current rate holds for the rest of the shift, downtime so far is
   representative, moved operators are fully trained for the new
   operation, and there is no rework, absence or style change.
   ===================================================================== */

import { IDLE_BELOW } from "./sampleData";

const round1 = (n) => Math.round(n * 10) / 10;

export function stationCapacity(s) {
  return (s.operators * 60) / s.smv * s.performance; // pcs/h at 100 % availability
}

/**
 * Full analysis of one line at the snapshot time.
 * `history` (optional) = the analysis of the line as it actually ran. A
 * balancing scenario can only change the REST of the shift, so output and
 * WIP so far are taken from history and only the rate going forward changes.
 */
export function analyzeLine(line, shift, history) {
  const elapsedH = shift.elapsedMin / 60;
  const remainingH = Math.max(0, (shift.workingMin - shift.elapsedMin) / 60);

  const base = line.stations.map((s) => {
    const capacity = stationCapacity(s);
    const availability = shift.elapsedMin > 0 ? Math.max(0, 1 - s.downtimeMin / shift.elapsedMin) : 1;
    return { ...s, capacity, availability, effCap: capacity * availability };
  });

  const rate = Math.min(...base.map((s) => s.effCap));
  const bIndex = base.findIndex((s) => s.effCap === rate);

  let upstream = Infinity;
  const stations = base.map((s, i) => {
    upstream = Math.min(upstream, s.effCap);
    const completed = Math.floor(upstream * elapsedH);
    const utilization = s.effCap > 0 ? rate / s.effCap : 0;
    const status = s.state === "stopped" ? "stopped" : utilization < IDLE_BELOW ? "idle" : "running";
    return { ...s, completed, utilization, status, isBottleneck: i === bIndex };
  });
  const limit = line.wipLimit ?? 48;
  for (let i = stations.length - 2; i >= 0; i--) stations[i].completed = Math.min(stations[i].completed, stations[i + 1].completed + limit);
  stations.forEach((s, i) => { s.wipBefore = i === 0 ? 0 : Math.max(0, stations[i - 1].completed - s.completed); });
  if (history) stations.forEach((s, i) => { s.completed = history.stations[i].completed; s.wipBefore = history.stations[i].wipBefore; });

  const output = stations[stations.length - 1].completed;
  const projected = Math.floor(output + rate * remainingH);
  const remainingToTarget = Math.max(0, line.dailyTarget - output);
  const minutesToTarget = rate > 0 ? (remainingToTarget / rate) * 60 : Infinity;
  const operators = stations.reduce((a, s) => a + s.operators, 0);
  const sumSmv = stations.reduce((a, s) => a + s.smv, 0);
  const maxCycle = Math.max(...stations.map((s) => s.smv / s.operators));

  return {
    lineId: line.id,
    stations,
    rate: round1(rate),
    bottleneck: stations[bIndex],
    output,
    target: line.dailyTarget,
    targetRate: round1(line.dailyTarget / (shift.workingMin / 60)),
    projected,
    gap: projected - line.dailyTarget, // negative = short of target at shift end
    targetAt: workingMinToClock(shift, shift.elapsedMin + minutesToTarget),
    operators,
    downtimeMin: stations.reduce((a, s) => a + s.downtimeMin, 0),
    balance: Math.round((sumSmv / (operators * maxCycle)) * 100),
    meanUtilization: stations.reduce((a, s) => a + s.utilization, 0) / stations.length,
  };
}

/**
 * Line-balancing scenario: repeat `moves` times — take one operator from the
 * station (with 2+ operators) whose loss keeps the line rate highest, and
 * give them to the current bottleneck. Stops early if a move would not help.
 */
export function balanceLine(line, shift, moves) {
  let stations = line.stations.map((s) => ({ ...s }));
  const log = [];
  for (let m = 0; m < moves; m++) {
    const now = analyzeLine({ ...line, stations }, shift);
    const b = now.stations.findIndex((s) => s.isBottleneck);
    let best = null;
    stations.forEach((d, i) => {
      if (i === b || d.operators < 2) return;
      const trial = stations.map((s, k) => (k === i ? { ...s, operators: s.operators - 1 } : k === b ? { ...s, operators: s.operators + 1 } : s));
      const r = analyzeLine({ ...line, stations: trial }, shift).rate;
      if (!best || r > best.rate) best = { rate: r, trial, from: stations[i], to: stations[b] };
    });
    if (!best || best.rate <= now.rate) break;
    stations = best.trial;
    log.push({ from: best.from.id, fromOp: best.from.op, to: best.to.id, toOp: best.to.op });
  }
  return { line: { ...line, stations }, moves: log };
}

/** Before/after for the selected line and scenario. */
export function runScenario(snapshot, lineId, scenarioId) {
  const line = snapshot.lines.find((l) => l.id === lineId) || snapshot.lines[0];
  const scenario = snapshot.scenarios.find((s) => s.id === scenarioId) || snapshot.scenarios[0];
  const before = analyzeLine(line, snapshot.shift);
  const balanced = balanceLine(line, snapshot.shift, scenario.moves);
  const after = analyzeLine(balanced.line, snapshot.shift, before);
  return { line, scenario, before, after, moves: balanced.moves };
}

/** Convert "working minutes since shift start" to a clock time, skipping the break. */
export function workingMinToClock(shift, workingMin) {
  if (!Number.isFinite(workingMin)) return { clock: "—", overtimeMin: Infinity, withinShift: false };
  const [h, m] = shift.start.split(":").map(Number);
  const overtimeMin = Math.max(0, Math.round(workingMin - shift.workingMin));
  const wall = workingMin + (workingMin > shift.breakStartMin ? shift.breakMin : 0);
  const total = Math.round(h * 60 + m + wall);
  const clock = `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  return { clock, overtimeMin, withinShift: overtimeMin === 0 };
}
